import React, { FunctionComponent, useEffect } from 'react';
import { OrderSummaryCardContainer } from '@Components/Card/cards/OrderSummary/OrderSummary.styled';
import Card from '@Components/Card/Card';
import ConditionalRender from '@Components/ConditionalRender/ConditionalRender';
import BusyIndicator from '@Components/BusyIndicator/BusyIndicator';
import { VIEW_CART_ITEMS } from '@Views/Cart/Cart';
import { MAKASIB_POINTS_PRODUCT_ID, MAKASIB_VOUCHER_PRODUCT_ID } from '@Config/app.config';
import { ICheckout as ICheckoutAction } from '@Redux/modules/checkout/actions';
import { getCartData, chargingLogging } from '@Views/Checkout/util';
import { isGreenZeroPayment } from '@Config/features.config';
import { useSelector } from 'react-redux';
import { remoteConfigSelector } from '@Redux/modules/settings/remoteConfig/selectors';

const OrderSummary: FunctionComponent<types.cards.IOrderSummaryCardProps> = ({
	params: { cart, contactDetails, paymentData, isSimplifiedCheckout = false, handleUpdatePaymentData },
}) => {
	const remoteFeatureConfig = useSelector(remoteConfigSelector);
	const DISABLE_GREEN_ZERO_PAYMENT = isGreenZeroPayment(remoteFeatureConfig);
	const { creditRating } = useSelector((state: types.redux.IState) => state.api.personalData);
	const isLogged = useSelector((state: types.redux.IState) => state.api.authentication.signedIn);
	const isMakasibItemInCart = !!cart.items.filter(
		(item) => item.productId === MAKASIB_POINTS_PRODUCT_ID || item.productId === MAKASIB_VOUCHER_PRODUCT_ID
	).length;
	const { totalProductPrice, isGreenXP, namab2b2cZeroUpfront, items, lastUpdateDate, orderId } = cart;
	const cartData = getCartData(items);
	const showPayment = (totalProductPrice !== '0' || isMakasibItemInCart) && !isGreenXP && !namab2b2cZeroUpfront;

	const orderTracking = () => {
		const payload = {
			DISABLE_GREEN_ZERO_PAYMENT,
			cartData,
			creditRating,
			ctaLabel: 'Page Load',
			installmentPeriod: items[0]?.installmentDuration,
			isGreenXP,
			isLogged: isLogged ? 'Yes' : 'No',
			isMakasibItemInCart,
			lastUpdateDate,
			namab2b2cZeroUpfront,
			orderId,
			step: 'OrderSummaryPage',
			totalProductPrice,
			...(isSimplifiedCheckout && {
				skipPaymentScreen: !showPayment,
			}),
		};
		chargingLogging(payload);
	};

	useEffect(() => {
		orderTracking();
	}, []);

	return (
		<OrderSummaryCardContainer>
			<BusyIndicator listener={VIEW_CART_ITEMS} skeleton="checkout4">
				<>
					<Card<types.cards.ICartItemsCardProps>
						card="cartItems"
						params={{
							cartItems: cart.items,
							withoutQuantityButtons: !isSimplifiedCheckout,
							tableMode: true,
							addToCart: false,
							summaryView: !isSimplifiedCheckout,

							isSimplifiedCheckout,
						}}
						marginBottom={16}
					/>

					{isSimplifiedCheckout && (
						<Card<types.cards.IContactDetailsCardProps>
							card="contactDisplayDetails"
							params={{ isSimplifiedCheckout }}
							marginBottom={16}
						/>
					)}

					<Card<types.cards.IShippingDetailsCardProps>
						card="shippingDetails"
						params={{
							isSimplifiedCheckout,
							contactDetails,
						}}
						marginBottom={16}
					/>

					<ConditionalRender
						show={showPayment}
						onTrue={() => (
							<Card<types.cards.IPaymentDetailsCardProps>
								card="paymentData"
								marginBottom={16}
								params={{ paymentData }}
								onEvent={(event, data: ICheckoutAction['payload']['paymentData']) => {
									if (
										event === 'updatePaymentData' &&
										typeof handleUpdatePaymentData === 'function'
									) {
										(handleUpdatePaymentData as (
											d: ICheckoutAction['payload']['paymentData']
										) => void)(data);
									}
								}}
							/>
						)}
					/>
					<Card<types.cards.ICartPaymentSummaryCardProps>
						card="cartPaymentSummary"
						params={{
							items: cart.items,
						}}
						marginBottom={16}
					/>
				</>
			</BusyIndicator>
		</OrderSummaryCardContainer>
	);
};

export default OrderSummary;
